<script setup>
import { ref, onMounted } from 'vue';

// import { gsap } from 'gsap';
import { GA } from '@/mixins/Ga';
import wysiwyg from '@/components/wysiwyg/Wysiwyg.vue';
import { useStore } from '@/store/Store.js';

const blocks = ref(null);
const store = useStore();

const ga = new GA({ category: 'endBody', action: 'clicInterne' });

const scrollX = ref(0);
const rootEl = ref(null);

defineProps({
    type: {
        type: String,
        default: '',
    },
    space: { type: Boolean, default: true },
    center: { type: Boolean, default: false },
    color: {
        type: String,
        default: '',
    },
    coverColor: {
        type: String,
        default: '',
    },
    titleTop: {
        type: String,
        default: '',
    },
    cta: {
        type: String,
        default: '',
    },
    ctaLink: {
        type: [String, Object],
        default: '',
    },
    ctaTarget: {
        type: String,
        default: '',
    },
    blogs: {
        type: Array,
        default: () => [],
    },
    gaLabel: {
        type: String,
        default: '',
    },
    wysiwygData: {
        type: String,
        default: '',
    },
});

onMounted(() => {
    if (!store.xl.value || !blocks.value) return;

    const client = blocks.value.getBoundingClientRect();
    scrollX.value = (window.innerWidth - blocks.value.scrollWidth - client.left) * 2;
});
</script>

<template>
    <div
        ref="rootEl"
        class="BlogFeaturedList px-6 py-0 print:hidden xl:px-20 xl:py-5 xl:pt-10"
        :class="['is-' + type]"
    >
        <template v-if="blogs && blogs.length">
            <div
                class="BlogFeaturedList__top relative mb-8 xl:mb-12"
                :class="{
                    'xl:text-right': type === 'home',
                    'text-left': type !== 'home',
                }"
            >
                <h2
                    class="h2 inline-block"
                    :class="['text-' + color]"
                >
                    {{ titleTop }}
                </h2>
            </div>
            <div
                v-if="wysiwygData"
                class="BlogFeaturedList__text mb-8 xl:mb-12"
            >
                <wysiwyg
                    class="h5"
                    :class="['text-' + color]"
                    :center="false"
                    :space="false"
                    :color="color"
                    :data="{ data: { wysiwyg: wysiwygData } }"
                />
            </div>
            <div class="BlogFeaturedList__container w-full">
                <div
                    ref="blocks"
                    class="BlogFeaturedList__blocks flex flex-col gap-6 xl:flex-row"
                >
                    <router-link
                        :to="setUri({ uri: blogs[0].uri })"
                        class="BlogFeaturedList__block group xl:h-124 xl:w-18/24"
                    >
                        <div
                            class="relative h-full"
                            v-on:click="ga.send({ label: blogs[0].title })"
                        >
                            <div class="pointer-events-none absolute bottom-5 left-6 z-2 max-w-[calc(100%-3rem)] font-bold md:bottom-7 md:left-8">
                                <div class="BlogFeaturedList__category pointer-events-none mb-1 mt-6 text-xxs leading-tight text-white sm:text-base md:text-lg">
                                    {{ blogs[0].category[0].title }}
                                </div>
                                <h5 class="pointer-events-none mb-4 text-base leading-tight text-white sm:text-xl md:text-3xl">
                                    {{ blogs[0].title }}
                                </h5>
                                <div class="pointer-events-none text-xxs leading-tight text-white sm:text-base md:text-lg">
                                    Lire l'article
                                    <Icon
                                        class="ml-2 inline-flex w-4 fill-current transition duration-500 group-hover:translate-x-2"
                                        type="arrow"
                                    />
                                </div>
                            </div>
                            <div class="BlogFeaturedList__cover relative z-1 h-full overflow-hidden rounded-xl">
                                <div class="absolute bottom-0 z-10 h-48 w-full bg-gradient-to-b from-black/0 to-black/50" />
                                <Cover
                                    class="relative h-full w-full"
                                    imgclass="can-hover:hover:scale-105"
                                    :clip-path="false"
                                    :color="coverColor"
                                    :data="blogs[0].cover"
                                />
                            </div>
                        </div>
                    </router-link>
                    <div class="BlogFeaturedList__right flex flex-col items-center gap-6 xl:w-8/24 xl:items-stretch">
                        <div class="flex h-full flex-col gap-6 md:flex-row md:flex-wrap xl:flex-col xl:flex-nowrap xl:justify-stretch">
                            <template
                                v-for="(blog, i) in blogs"
                                :key="blog.uri"
                            >
                                <router-link
                                    v-if="i !== 0"
                                    :to="setUri({ uri: blog.uri })"
                                    class="BlogFeaturedList__block flex w-full items-center justify-center gap-4 rounded-xl transition duration-500 md:-m-4 md:w-1/3 md:grow md:items-center md:p-4 md:hover:bg-white xl:w-auto"
                                    v-on:click="ga.send({ label: blog.title })"
                                >
                                    <div class="BlogFeaturedList__cover flex h-24 w-24 shrink-0 grow-0 xl:h-full xl:w-36 xl:justify-start">
                                        <Cover
                                            class="relative h-full w-full"
                                            imgclass="can-hover:hover:scale-110"
                                            :clip-path="false"
                                            :color="coverColor"
                                            :data="blog.cover"
                                        />
                                    </div>
                                    <div class="flex w-full grow flex-col md:w-auto">
                                        <div
                                            class="BlogFeaturedList__category text-xxs font-bold"
                                            :class="['text-' + color]"
                                        >
                                            <span
                                                v-if="blog.day && blog.month && blog.year"
                                                class="Blogs__date lowercase"
                                                >{{ blog.day }} {{ FRMonth[blog.month - 1] }} {{ blog.year }} •
                                            </span>
                                            {{ blog.category[0].title }}
                                        </div>
                                        <h5
                                            class="line-clamp-5 text-xs font-bold leading-tight"
                                            :class="['text-' + color]"
                                        >
                                            {{ blog.title }}
                                        </h5>
                                    </div>
                                </router-link>
                            </template>
                        </div>
                        <a
                            v-if="ctaLink && typeof ctaLink == 'string' && ctaLink.startsWith('http')"
                            class="block whitespace-nowrap"
                            :href="ctaLink"
                            :target="ctaTarget"
                        >
                            <Btn
                                :text="cta"
                                size="small"
                                class="w-full text-white"
                                :class="['bg-' + color]"
                            />
                        </a>
                        <router-link
                            v-else-if="ctaLink"
                            class="whitespace-nowrap"
                            :to="ctaLink"
                            :target="ctaTarget"
                            v-on:click="ga.send({ label: gaLabel })"
                        >
                            <Btn
                                :text="cta"
                                size="small"
                                class="w-full text-white"
                                :class="['bg-' + color]"
                            />
                        </router-link>
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>
